import { gardenMetrics, graphLinks, notes, sampleMomentCategories, sampleMoments, sourceNodes } from "./sampleData";
import { buildCategoryTree, todayLocalDay } from "./moments";
import type {
  Attachment,
  DueReview,
  EntityLink,
  ExtractionCandidate,
  FocusSession,
  GardenStats,
  Moment,
  MomentCategory,
  MomentCategoryKind,
  MomentCategoryNode,
  MomentsBootstrap,
  MomentStats,
  NoteCard,
  NoteKind,
  PublicAuthor,
  PublicItem,
  RelationDetail,
  RelationTarget,
  SearchResult,
  SourceKind
} from "../types";

type SessionPayload = {
  authenticated: boolean;
  user: { id: string; name: string | null; email: string | null } | null;
  mode?: string;
};

const localDevSession: SessionPayload = {
  authenticated: true,
  user: { id: "local_user", name: "Human Note Local User", email: "bangbangnguyen2610@gmail.com" },
  mode: "vite-local-bypass"
};

function isFrontendLocalDev() {
  if (!import.meta.env.DEV) {
    return false;
  }
  if (typeof window === "undefined") {
    return true;
  }
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function isHtmlFallback(response: Response) {
  return (response.headers.get("content-type") ?? "").includes("text/html");
}

function isLocalApiFallbackError(error: unknown) {
  if (!isFrontendLocalDev()) {
    return false;
  }
  if (error instanceof TypeError || error instanceof SyntaxError) {
    return true;
  }
  return error instanceof Error && error.message === "Vite dev API fallback";
}

async function readApiJson<T>(response: Response): Promise<T> {
  if (isHtmlFallback(response)) {
    throw new Error("Vite dev API fallback");
  }
  return response.json() as Promise<T>;
}

function localSourceSubtreeIds(sourceId: string) {
  const ids = new Set<string>([sourceId]);
  let changed = true;

  while (changed) {
    changed = false;
    for (const source of sourceNodes) {
      if (source.parentId && ids.has(source.parentId) && !ids.has(source.id)) {
        ids.add(source.id);
        changed = true;
      }
    }
  }

  return ids;
}

export async function loadSession(): Promise<SessionPayload> {
  try {
    const response = await fetch("/api/auth/session");

    if (isFrontendLocalDev() && isHtmlFallback(response)) {
      return localDevSession;
    }

    if (!response.ok) {
      throw new Error(`Session check failed: ${response.status}`);
    }

    return readApiJson<SessionPayload>(response);
  } catch (error) {
    if (isLocalApiFallbackError(error)) {
      return localDevSession;
    }
    throw error;
  }
}

export async function loginWithPasscode(passcode: string) {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ passcode })
  });

  if (!response.ok) {
    throw new Error(response.status === 401 ? "Invalid passcode" : `Login failed: ${response.status}`);
  }

  return response.json();
}

async function readAuthError(response: Response, fallback: string) {
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  const map: Record<string, string> = {
    username_taken: "Tên người dùng đã có người dùng.",
    email_taken: "Email này đã được đăng ký.",
    invalid_credentials: "Email hoặc mật khẩu không đúng.",
    invalid_register: "Thông tin đăng ký chưa hợp lệ.",
    invalid_login: "Thông tin đăng nhập chưa hợp lệ."
  };
  return body.error && map[body.error] ? map[body.error] : `${fallback}: ${response.status}`;
}

export async function registerAccount(input: { email: string; username: string; password: string }) {
  const response = await fetch("/api/auth/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    throw new Error(await readAuthError(response, "Đăng ký thất bại"));
  }
  return response.json();
}

export async function loginWithCredentials(input: { email: string; password: string }) {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    throw new Error(await readAuthError(response, "Đăng nhập thất bại"));
  }
  return response.json();
}

export async function setObjectVisibility(id: string, visibility: "public" | "private") {
  const response = await fetch(`/api/objects/${id}/visibility`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ visibility })
  });
  if (!response.ok) {
    throw new Error(`Đổi hiển thị thất bại: ${response.status}`);
  }
  return response.json() as Promise<{ data: { id: string; visibility: string } }>;
}

export async function loadPublicFeed(author?: string) {
  const url = author ? `/api/public/feed?author=${encodeURIComponent(author)}` : "/api/public/feed";
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Tải feed thất bại: ${response.status}`);
  }
  return response.json() as Promise<{ data: PublicItem[] }>;
}

export async function loadPublicAuthors() {
  const response = await fetch("/api/public/authors");
  if (!response.ok) {
    throw new Error(`Tải tác giả thất bại: ${response.status}`);
  }
  return response.json() as Promise<{ data: PublicAuthor[] }>;
}

export async function loadPublicObject(id: string) {
  const response = await fetch(`/api/public/objects/${id}`);
  if (!response.ok) {
    throw new Error(`not_found`);
  }
  return response.json() as Promise<{ data: PublicItem }>;
}

export async function forgotPassword(email: string) {
  const response = await fetch("/api/auth/forgot-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email })
  });
  if (!response.ok) {
    throw new Error(`Gửi yêu cầu thất bại: ${response.status}`);
  }
  return response.json() as Promise<{ ok: boolean; devResetUrl?: string }>;
}

export async function resetPassword(token: string, password: string) {
  const response = await fetch("/api/auth/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password })
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error === "invalid_or_expired_token" ? "Link đặt lại đã hết hạn hoặc không hợp lệ." : `Đặt lại thất bại: ${response.status}`);
  }
  return response.json();
}

export async function changePassword(input: { currentPassword?: string; newPassword: string }) {
  const response = await fetch("/api/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error === "wrong_current_password" ? "Mật khẩu hiện tại không đúng." : `Đổi mật khẩu thất bại: ${response.status}`);
  }
  return response.json();
}

export async function loadDueReviews() {
  const response = await fetch("/api/review/due");
  if (!response.ok) {
    throw new Error(`Tải ôn tập thất bại: ${response.status}`);
  }
  return response.json() as Promise<{ data: DueReview[] }>;
}

export async function markReviewed(objectId: string) {
  const response = await fetch("/api/review", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ objectId })
  });
  if (!response.ok) {
    throw new Error(`Đánh dấu ôn thất bại: ${response.status}`);
  }
  return response.json();
}

export async function logoutSession() {
  try {
    const response = await fetch("/api/auth/logout", {
      method: "POST"
    });

    if (isFrontendLocalDev() && isHtmlFallback(response)) {
      return { authenticated: false, mode: "local-mock" };
    }

    if (!response.ok) {
      throw new Error(`Logout failed: ${response.status}`);
    }

    return readApiJson(response);
  } catch (error) {
    if (isLocalApiFallbackError(error)) {
      return { authenticated: false, mode: "local-mock" };
    }
    throw error;
  }
}

export async function loadBootstrap() {
  try {
    const response = await fetch("/api/bootstrap");
    if (response.status === 401) {
      throw new Error("Unauthorized");
    }

    if (!response.ok) {
      throw new Error(`Bootstrap failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error && error.message === "Unauthorized") {
      throw error;
    }

    return {
      sourceNodes,
      notes,
      gardenMetrics,
      graphLinks,
      ontologyTypes: [],
      relationTypes: [],
      mode: "local-mock"
    };
  }
}

export async function loadSourceNotes(sourceId: string): Promise<{ data: NoteCard[]; sourceId: string; mode?: string }> {
  try {
    const response = await fetch(`/api/sources/${encodeURIComponent(sourceId)}/notes`);
    if (isFrontendLocalDev() && isHtmlFallback(response)) {
      const sourceIds = localSourceSubtreeIds(sourceId);
      return { data: notes.filter((note) => sourceIds.has(note.sourceId)), sourceId, mode: "local-mock" };
    }
    if (response.status === 401) {
      throw new Error("Unauthorized");
    }

    if (!response.ok) {
      throw new Error(`Load source notes failed: ${response.status}`);
    }

    return readApiJson(response);
  } catch (error) {
    if (isLocalApiFallbackError(error)) {
      const sourceIds = localSourceSubtreeIds(sourceId);
      return { data: notes.filter((note) => sourceIds.has(note.sourceId)), sourceId, mode: "local-mock" };
    }
    throw error;
  }
}

const emptyGardenStats: GardenStats = {
  mode: "local-mock",
  counts: { notes: 0, focusSessions: 0, connections: 0, reviewed: 0 },
  points: { total: 0, weights: { note: 1, focus: 2, connection: 1, reviewed: 1 } },
  stage: 0,
  stageLabel: "barren",
  nextStageAt: 1,
  streak: { current: 0, longest: 0, lastActiveDay: null },
  activeDays: 0
};

export async function loadGardenStats(): Promise<GardenStats> {
  try {
    const response = await fetch("/api/garden-stats");
    if (response.status === 401) {
      throw new Error("Unauthorized");
    }
    if (!response.ok) {
      throw new Error(`Garden stats failed: ${response.status}`);
    }
    return (await response.json()) as GardenStats;
  } catch (error) {
    if (error instanceof Error && error.message === "Unauthorized") {
      throw error;
    }
    return emptyGardenStats;
  }
}

export async function captureNote(input: {
  kind: NoteKind;
  content: string;
  sourceId: string;
  relationType?: string;
  relationTargetId?: string;
  status?: string;
  tags?: string[];
  properties?: Record<string, unknown>;
}) {
  const response = await fetch("/api/capture", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      workspaceId: "local_workspace",
      ...input
    })
  });

  if (!response.ok) {
    throw new Error(`Capture failed: ${response.status}`);
  }

  return response.json();
}

export async function updateNote(id: string, input: { content: string }) {
  const response = await fetch(`/api/notes/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Update note failed: ${response.status}`);
  }

  return response.json() as Promise<{ data: { id: string; title: string; content: string }; mode: string }>;
}

export async function createSource(input: {
  title: string;
  kind: SourceKind;
  parentId?: string;
  progress?: number;
  status?: string;
  tags?: string[];
  properties?: Record<string, unknown>;
}) {
  const response = await fetch("/api/sources", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      workspaceId: "local_workspace",
      progress: 0,
      ...input
    })
  });

  if (!response.ok) {
    throw new Error(`Create source failed: ${response.status}`);
  }

  return response.json();
}

export async function createOutput(input: {
  title: string;
  content: string;
  inputObjectIds: string[];
  parentSourceId?: string;
  tags?: string[];
  properties?: Record<string, unknown>;
}) {
  const response = await fetch("/api/outputs", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      workspaceId: "local_workspace",
      ...input
    })
  });

  if (!response.ok) {
    throw new Error(`Create output failed: ${response.status}`);
  }

  return response.json() as Promise<{ data: { outputId: string; blockId: string; relationIds: string[]; parentRelationId: string | null }; mode: string }>;
}

export async function updateOutput(id: string, input: { title: string; content: string }) {
  const response = await fetch(`/api/outputs/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Update output failed: ${response.status}`);
  }

  return response.json() as Promise<{ data: { id: string; title: string; content: string }; mode: string }>;
}

export async function updateSource(
  id: string,
  input: {
    title?: string;
    kind?: SourceKind;
    progress?: number;
    parentId?: string | null;
    status?: string;
    properties?: Record<string, unknown>;
  }
) {
  const response = await fetch(`/api/sources/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Update source failed: ${response.status}`);
  }

  return response.json();
}

// Change the lifecycle/status of any object (note or source child). Free-form so the UI
// can add custom statuses; the Worker merges it into properties_json.lifecycle.
export async function updateObjectStatus(id: string, status: string) {
  const response = await fetch(`/api/objects/${id}/status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ status })
  });

  if (!response.ok) {
    throw new Error(`Update status failed: ${response.status}`);
  }

  return response.json();
}

// Set the tags (free-form categories) of any object (note or source). The Worker normalizes
// + merges them into properties_json.tags.
export async function updateObjectTags(id: string, tags: string[]) {
  const response = await fetch(`/api/objects/${id}/tags`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ tags })
  });

  if (!response.ok) {
    throw new Error(`Update tags failed: ${response.status}`);
  }

  return response.json();
}

export async function archiveSource(id: string) {
  const response = await fetch(`/api/sources/${id}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    if (response.status === 409) {
      throw new Error("Không thể xóa mục đang có liên kết bên trong");
    }

    throw new Error(`Archive source failed: ${response.status}`);
  }

  return response.json();
}

export async function createRelation(input: {
  sourceObjectId: string;
  relationType: string;
  targetObjectId: string;
  properties?: Record<string, unknown>;
}) {
  const response = await fetch("/api/relations", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      workspaceId: "local_workspace",
      ...input
    })
  });

  if (!response.ok) {
    throw new Error(`Create relation failed: ${response.status}`);
  }

  return response.json() as Promise<{ data: RelationDetail | null; mode: "persisted" | "existing" | "dry-run" }>;
}

export async function findRelationTargets(input: { objectId: string; q?: string; limit?: number }) {
  const params = new URLSearchParams({ objectId: input.objectId });
  if (input.q?.trim()) {
    params.set("q", input.q.trim());
  }
  if (input.limit) {
    params.set("limit", String(input.limit));
  }

  const response = await fetch(`/api/relations/find-targets?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Find relation targets failed: ${response.status}`);
  }

  return response.json() as Promise<{ data: RelationTarget[]; mode: string }>;
}

export async function updateRelation(id: string, input: { relationType: string }) {
  const response = await fetch(`/api/relations/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Update relation failed: ${response.status}`);
  }

  return response.json() as Promise<{ data: RelationDetail | null; mode: "persisted" | "existing" | "dry-run" }>;
}

export async function deleteRelation(id: string) {
  const response = await fetch(`/api/relations/${id}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error(`Delete relation failed: ${response.status}`);
  }

  return response.json() as Promise<{ data: { relationId: string; changed?: number }; mode: "persisted" | "dry-run" }>;
}

export async function loadCandidates(): Promise<{ data: ExtractionCandidate[]; mode?: string }> {
  try {
    const response = await fetch("/api/ai/candidates?status=pending");

    if (isFrontendLocalDev() && isHtmlFallback(response)) {
      return { data: [], mode: "local-mock" };
    }

    if (!response.ok) {
      throw new Error(`Load candidates failed: ${response.status}`);
    }

    return readApiJson<{ data: ExtractionCandidate[]; mode?: string }>(response);
  } catch (error) {
    if (isLocalApiFallbackError(error)) {
      return { data: [], mode: "local-mock" };
    }
    throw error;
  }
}

export async function suggestForNote(objectId: string) {
  const response = await fetch("/api/ai/suggest", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ objectId })
  });

  if (!response.ok) {
    throw new Error(`Suggest failed: ${response.status}`);
  }

  return response.json();
}

export async function reviewCandidate(id: string, decision: "accepted" | "rejected") {
  const response = await fetch(`/api/ai/candidates/${id}/review`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ decision })
  });

  if (!response.ok) {
    throw new Error(`Review candidate failed: ${response.status}`);
  }

  return response.json();
}

export async function loadAttachments(objectId: string): Promise<{ data: Attachment[]; mode?: string }> {
  try {
    const response = await fetch(`/api/attachments?objectId=${encodeURIComponent(objectId)}`);

    if (isFrontendLocalDev() && isHtmlFallback(response)) {
      return { data: [], mode: "local-mock" };
    }

    if (!response.ok) {
      throw new Error(`Load attachments failed: ${response.status}`);
    }

    return readApiJson<{ data: Attachment[]; mode?: string }>(response);
  } catch (error) {
    if (isLocalApiFallbackError(error)) {
      return { data: [], mode: "local-mock" };
    }
    throw error;
  }
}

export type AttachmentUploadOptions = {
  attachmentKind?: "image" | "file";
  source?: "picker" | "paste" | "drop";
  sortOrder?: number;
};

export async function uploadAttachment(objectId: string, file: File, options: AttachmentUploadOptions = {}) {
  const form = new FormData();
  form.append("objectId", objectId);
  form.append("file", file);
  if (options.attachmentKind) {
    form.append("attachmentKind", options.attachmentKind);
  }
  if (options.source) {
    form.append("source", options.source);
  }
  if (typeof options.sortOrder === "number") {
    form.append("sortOrder", String(options.sortOrder));
  }

  const response = await fetch("/api/attachments", {
    method: "POST",
    body: form
  });

  if (!response.ok) {
    throw new Error(`Upload attachment failed: ${response.status}`);
  }

  return response.json();
}

export async function dreamingRecall(input: { mode: "session" | "due"; since?: string; until?: string; context?: string }) {
  const response = await fetch("/api/dreaming", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Dreaming failed: ${response.status}`);
  }

  return response.json() as Promise<{
    data?: { recall: string; reflect: string; encourage: string; noteCount: number; mode: string; source?: "workers-ai" | "fallback"; error?: string };
    error?: string;
  }>;
}

export async function listFocusSessions() {
  const response = await fetch("/api/focus-sessions");
  if (!response.ok) {
    throw new Error(`List focus sessions failed: ${response.status}`);
  }
  return response.json() as Promise<{ data: FocusSession[] }>;
}

export async function createFocusSession(input: {
  startedAt: string;
  stoppedAt: string;
  workMinutes: number;
  elapsedSeconds: number;
}) {
  const response = await fetch("/api/focus-sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    throw new Error(`Create focus session failed: ${response.status}`);
  }
  return response.json() as Promise<{ data: { id: string } }>;
}

export async function updateFocusSession(
  id: string,
  input: {
    context?: string;
    mode?: "session" | "due";
    noteCount?: number;
    recall?: string;
    reflect?: string;
    encourage?: string;
  }
) {
  const response = await fetch(`/api/focus-sessions/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    throw new Error(`Update focus session failed: ${response.status}`);
  }
  return response.json() as Promise<{ data: { id: string } }>;
}

export async function transcribeAttachment(attachmentId: string) {
  const response = await fetch(`/api/attachments/${attachmentId}/transcribe`, {
    method: "POST"
  });

  if (!response.ok) {
    throw new Error(`Transcribe failed: ${response.status}`);
  }

  return response.json() as Promise<{ data?: { text: string; objectId: string }; error?: string }>;
}

export async function deleteAttachment(id: string) {
  const response = await fetch(`/api/attachments/${id}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error(`Delete attachment failed: ${response.status}`);
  }

  return response.json();
}

export async function loadLinks(objectId: string): Promise<{ data: EntityLink[]; mode?: string }> {
  try {
    const response = await fetch(`/api/links?objectId=${encodeURIComponent(objectId)}`);

    if (isFrontendLocalDev() && isHtmlFallback(response)) {
      return { data: [], mode: "local-mock" };
    }

    if (!response.ok) {
      throw new Error(`Load links failed: ${response.status}`);
    }

    return readApiJson<{ data: EntityLink[]; mode?: string }>(response);
  } catch (error) {
    if (isLocalApiFallbackError(error)) {
      return { data: [], mode: "local-mock" };
    }
    throw error;
  }
}

export async function createLink(input: { objectId: string; url: string; label?: string }) {
  const response = await fetch("/api/links", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Create link failed: ${response.status}`);
  }

  return response.json();
}

export async function deleteLink(id: string) {
  const response = await fetch(`/api/links/${id}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error(`Delete link failed: ${response.status}`);
  }

  return response.json();
}

export async function reindexSearch() {
  const response = await fetch("/api/search/reindex", {
    method: "POST"
  });

  if (!response.ok) {
    throw new Error(`Reindex search failed: ${response.status}`);
  }

  return response.json();
}

export async function searchObjects(query: string): Promise<{ data: SearchResult[]; mode?: string }> {
  try {
    const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);

    if (isFrontendLocalDev() && isHtmlFallback(response)) {
      const normalized = query.trim().toLowerCase();
      const sourceResults = sourceNodes
        .filter((source) => source.title.toLowerCase().includes(normalized))
        .map((source) => ({ id: source.id, title: source.title, kind: source.kind, content: source.content ?? "", score: 1 }));
      const noteResults = notes
        .filter((note) => `${note.title} ${note.content}`.toLowerCase().includes(normalized))
        .map((note) => ({ id: note.id, title: note.title, kind: note.kind, content: note.content, score: 1 }));
      return { data: [...sourceResults, ...noteResults].slice(0, 20), mode: "local-mock" };
    }

    if (!response.ok) {
      throw new Error(`Search failed: ${response.status}`);
    }

    return readApiJson<{ data: SearchResult[]; mode?: string }>(response);
  } catch (error) {
    if (isLocalApiFallbackError(error)) {
      return { data: [], mode: "local-mock" };
    }
    throw error;
  }
}

export async function loadOntology() {
  const response = await fetch("/api/ontology");

  if (!response.ok) {
    throw new Error(`Load ontology failed: ${response.status}`);
  }

  return response.json();
}

export async function createOntologyType(input: {
  slug: string;
  name: string;
  description?: string;
  parentTypeId?: string | null;
}) {
  const response = await fetch("/api/ontology/types", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Create entity type failed: ${response.status}`);
  }

  return response.json();
}

export async function updateOntologyType(id: string, input: { name?: string; description?: string; parentTypeId?: string | null }) {
  const response = await fetch(`/api/ontology/types/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(response.status === 409 ? "System type is read-only" : `Update entity type failed: ${response.status}`);
  }

  return response.json();
}

export async function createRelationType(input: {
  slug: string;
  name: string;
  nameVi?: string;
  description?: string;
  descriptionVi?: string;
  sourceTypeId?: string | null;
  targetTypeId?: string | null;
  inverseSlug?: string | null;
  directional?: boolean;
}) {
  const response = await fetch("/api/ontology/relations", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Create relation type failed: ${response.status}`);
  }

  return response.json();
}

export async function updateRelationType(
  id: string,
    input: {
      name?: string;
      nameVi?: string;
      description?: string;
      descriptionVi?: string;
      sourceTypeId?: string | null;
      targetTypeId?: string | null;
    inverseSlug?: string | null;
    directional?: boolean;
  }
) {
  const response = await fetch(`/api/ontology/relations/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(response.status === 409 ? "System relation is read-only" : `Update relation type failed: ${response.status}`);
  }

  return response.json();
}

export async function loadReadAnchors(sourceId: string) {
  const response = await fetch(`/api/read/${encodeURIComponent(sourceId)}/anchors`);

  if (!response.ok) {
    throw new Error(`Load read anchors failed: ${response.status}`);
  }

  return response.json();
}

export async function resolveReadUrl(url: string) {
  const response = await fetch(`/api/read/resolve?url=${encodeURIComponent(url)}`);

  if (!response.ok) {
    throw new Error(`Resolve read URL failed: ${response.status}`);
  }

  return response.json();
}

export async function createReadAnchor(
  sourceId: string,
  input: {
    sourceUrl?: string;
    quoteText: string;
    prefix?: string;
    suffix?: string;
    textStart?: number;
    textEnd?: number;
    cssSelector?: string;
    noteTitle?: string;
    noteContent?: string;
  }
) {
  const response = await fetch(`/api/read/${encodeURIComponent(sourceId)}/anchors`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    throw new Error(`Create read anchor failed: ${response.status}`);
  }

  return response.json();
}

export async function deleteReadAnchor(id: string) {
  const response = await fetch(`/api/read/anchors/${encodeURIComponent(id)}`, {
    method: "DELETE"
  });

  if (!response.ok) {
    throw new Error(`Delete read anchor failed: ${response.status}`);
  }

  return response.json();
}

// ---- Moments side-branch (Locket-style capture + routine/habit tracking) ----

export function momentPhotoUrl(id: string): string {
  return `/api/moments/${encodeURIComponent(id)}/photo`;
}

// Phone/camera view loads this: categories + recent moments + today's stats. Falls back to a
// local mock (mirrors the API shape) when the API is unreachable, like loadBootstrap.
export async function loadMomentsBootstrap(day: string = todayLocalDay()): Promise<MomentsBootstrap> {
  try {
    const response = await fetch(`/api/moments/bootstrap?day=${encodeURIComponent(day)}`);
    if (response.status === 401) {
      throw new Error("Unauthorized");
    }
    if (!response.ok) {
      throw new Error(`Moments bootstrap failed: ${response.status}`);
    }
    return (await response.json()) as MomentsBootstrap;
  } catch (error) {
    if (error instanceof Error && error.message === "Unauthorized") {
      throw error;
    }
    const categories = buildCategoryTree(sampleMomentCategories);
    return {
      categories,
      moments: sampleMoments,
      stats: { timeline: sampleMoments, perCategory: [], routineCompletion: { done: 0, total: 0 }, habitStreaks: [] },
      mode: "local-mock"
    };
  }
}

export async function listMomentCategories(): Promise<{ data: MomentCategoryNode[]; mode?: string }> {
  const response = await fetch("/api/moment-categories");
  if (!response.ok) {
    throw new Error(`List moment categories failed: ${response.status}`);
  }
  return response.json();
}

export async function createMomentCategory(input: {
  parentId?: string | null;
  kind: MomentCategoryKind;
  name: string;
  icon?: string | null;
  color?: string | null;
  sortOrder?: number;
}): Promise<{ data: MomentCategory }> {
  const response = await fetch("/api/moment-categories", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    throw new Error(`Create moment category failed: ${response.status}`);
  }
  return response.json();
}

export async function updateMomentCategory(
  id: string,
  patch: {
    name?: string;
    icon?: string | null;
    color?: string | null;
    parentId?: string | null;
    sortOrder?: number;
    status?: "active" | "archived";
    properties?: Record<string, unknown>;
  }
): Promise<{ data: { id: string } }> {
  const response = await fetch(`/api/moment-categories/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch)
  });
  if (!response.ok) {
    throw new Error(`Update moment category failed: ${response.status}`);
  }
  return response.json();
}

export async function deleteMomentCategory(id: string): Promise<{ data: { id: string } }> {
  const response = await fetch(`/api/moment-categories/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!response.ok) {
    throw new Error(`Delete moment category failed: ${response.status}`);
  }
  return response.json();
}

export async function reorderMomentCategories(
  items: Array<{ id: string; sortOrder: number; parentId?: string | null }>
): Promise<{ data: { count: number } }> {
  const response = await fetch("/api/moment-categories/reorder", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items })
  });
  if (!response.ok) {
    throw new Error(`Reorder moment categories failed: ${response.status}`);
  }
  return response.json();
}

// Single multipart round-trip: stores the photo in R2 + inserts the moment row. loggedAt/localDay
// default to now/device-local when omitted.
export async function captureMoment(input: {
  file: File;
  categoryId?: string | null;
  loggedAt?: string;
  localDay?: string;
  note?: string;
}): Promise<{ data: { id: string; photoUrl: string; loggedAt: string; localDay: string; categoryId: string | null } }> {
  const form = new FormData();
  form.append("file", input.file);
  if (input.categoryId) {
    form.append("categoryId", input.categoryId);
  }
  form.append("loggedAt", input.loggedAt ?? new Date().toISOString());
  form.append("localDay", input.localDay ?? todayLocalDay());
  if (input.note) {
    form.append("note", input.note);
  }
  const response = await fetch("/api/moments", { method: "POST", body: form });
  if (!response.ok) {
    throw new Error(`Capture moment failed: ${response.status}`);
  }
  return response.json();
}

export async function listMoments(filters: {
  categoryId?: string;
  from?: string;
  to?: string;
  limit?: number;
} = {}): Promise<{ data: Moment[] }> {
  const params = new URLSearchParams();
  if (filters.categoryId) params.set("categoryId", filters.categoryId);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (typeof filters.limit === "number") params.set("limit", String(filters.limit));
  const query = params.toString();
  const response = await fetch(`/api/moments${query ? `?${query}` : ""}`);
  if (!response.ok) {
    throw new Error(`List moments failed: ${response.status}`);
  }
  return response.json();
}

export async function updateMoment(
  id: string,
  patch: { categoryId?: string | null; loggedAt?: string; localDay?: string; note?: string | null; properties?: Record<string, unknown> }
): Promise<{ data: { id: string } }> {
  const response = await fetch(`/api/moments/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch)
  });
  if (!response.ok) {
    throw new Error(`Update moment failed: ${response.status}`);
  }
  return response.json();
}

export async function deleteMoment(id: string): Promise<{ data: { id: string } }> {
  const response = await fetch(`/api/moments/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!response.ok) {
    throw new Error(`Delete moment failed: ${response.status}`);
  }
  return response.json();
}

export async function momentStats(range: { from?: string; to?: string; day?: string } = {}): Promise<{ data: MomentStats }> {
  const params = new URLSearchParams();
  if (range.from) params.set("from", range.from);
  if (range.to) params.set("to", range.to);
  if (range.day) params.set("day", range.day);
  const query = params.toString();
  const response = await fetch(`/api/moments/stats${query ? `?${query}` : ""}`);
  if (!response.ok) {
    throw new Error(`Moment stats failed: ${response.status}`);
  }
  return response.json();
}
